import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Database, ShieldCheck, ShieldOff, FileText, Coins, Workflow, ArrowUpRight } from "lucide-react";
import StatusPill from "@/components/aieco/StatusPill";
import { inventory, agriBandFor } from "@/data/mockData";

const RISK_TONE = { Low: "success", Medium: "warning", High: "critical" };

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
        <Icon size={12} strokeWidth={1.8} />
        {title}
      </div>
      {children}
    </div>
  );
}

function Chip({ children, tone = "neutral", mono = true }) {
  const tones = {
    neutral: "border-white/8 bg-white/[0.03] text-slate-300",
    success: "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
    critical: "border-red-500/25 bg-red-500/10 text-red-300",
    info: "border-[#5B8DEF]/25 bg-[#5B8DEF]/10 text-[#bcd2ff]",
  };
  return (
    <span className={`rounded-md border px-2 py-1 text-[11px] ${tones[tone]} ${mono ? "font-mono" : ""}`}>
      {children}
    </span>
  );
}

export default function AiInventory() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid="page-inventory">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{t("inventory.subtitle")}</div>
          <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
            {t("inventory.title")}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            {t("inventory.lede")}
          </p>
        </div>
      </motion.div>

      <div className="mt-8 grid grid-cols-1 gap-5 xl:grid-cols-3">
        {inventory.map((a, i) => {
          const band = agriBandFor(a.agri);
          return (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 * i }}
              className="card-eco card-eco-hover flex flex-col p-6"
              data-testid={`inventory-card-${a.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/5 bg-gradient-to-br from-[#5B8DEF]/15 to-[#22C55E]/[0.06] text-[#5B8DEF]">
                    <Workflow size={18} strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="font-display text-base font-medium text-white">{a.name}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{a.purpose}</div>
                  </div>
                </div>
                <StatusPill tone={RISK_TONE[a.risk]}>{a.risk} risk</StatusPill>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">AGRI™</div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-mono text-2xl font-light text-white">{a.agri}</span>
                    <span className="text-[11px]" style={{ color: band.color }}>{band.label}</span>
                  </div>
                </div>
                <div className="rounded-lg border border-white/5 bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Tokens / day</div>
                  <div className="mt-1 font-mono text-2xl font-light text-white">{a.tokens}</div>
                </div>
              </div>

              <div className="mt-5 space-y-4">
                <Section title="Allowed Actions" icon={Workflow} testid={`section-actions-${a.id}`}>
                  <div className="flex flex-wrap gap-1.5">
                    {a.allowedActions.map((x) => (
                      <Chip key={x} tone="info">{x}</Chip>
                    ))}
                  </div>
                </Section>

                <Section title="Allowed Data" icon={Database} testid={`section-allowed-${a.id}`}>
                  <div className="flex flex-wrap gap-1.5">
                    {a.allowedData.map((x) => (
                      <Chip key={x} tone="success">
                        <span className="mr-1.5 text-emerald-400">●</span>{x}
                      </Chip>
                    ))}
                  </div>
                </Section>

                <Section title="Blocked Data" icon={ShieldOff} testid={`section-blocked-${a.id}`}>
                  <div className="flex flex-wrap gap-1.5">
                    {a.blockedData.map((x) => (
                      <Chip key={x} tone="critical">
                        <span className="mr-1.5 text-red-400">●</span>{x}
                      </Chip>
                    ))}
                  </div>
                </Section>

                <Section title="Policies" icon={FileText} testid={`section-policies-${a.id}`}>
                  <ul className="space-y-1.5 text-[12px] text-slate-300">
                    {a.policies.map((p, idx) => (
                      <li key={idx} className="flex gap-2">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#5B8DEF]" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </Section>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <ShieldCheck size={12} className="text-emerald-400" />
                  Policy enforced at runtime
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/inventory/${a.id}/policy`}
                    data-testid={`edit-policy-${a.id}`}
                    className="rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-1.5 text-[11px] text-slate-200 hover:border-white/10"
                  >
                    Edit policy
                  </Link>
                  <Link
                    to={`/inventory/${a.id}`}
                    data-testid={`open-agent-${a.id}`}
                    className="inline-flex items-center gap-1 rounded-lg bg-[#5B8DEF]/15 px-2.5 py-1.5 text-[11px] font-medium text-[#cfe0ff] hover:bg-[#5B8DEF]/25"
                  >
                    Open <ArrowUpRight size={12} />
                  </Link>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
